import React, { FunctionComponent, memo, useCallback } from "react";
import { Grid, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Helmet } from "react-helmet";
import { Input, Singleselect, ListOptionBase } from "@shared/components";
import ExtractionAwareInput from "@investorRequest/components/ExtractionAwareInput";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import FurtherCreditFields from "@investorRequest/components/FurtherCreditFields";
import IntermediaryFields from "@investorRequest/components/IntermediaryFields";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import { fieldKeys } from "@investorRequest/constants/fieldKeys";
import { useDropdownOverflowTooltip } from "@shared/hooks/useDropdownOverflowTooltip";
import { getDropdownWithIconStyles } from "@shared/styles/dropdownWithIconStyles";
import { Nullable } from "@shared/models/general";
import {
  sanitizeAsciiName,
  sanitizeAccountNumberInput,
  sanitizeSwiftId,
  sanitizeIBAN,
  sanitizeBankId,
  sanitizeBranchId,
  isValidSwiftId,
  isValidIBAN,
  isValidBankId,
  isValidBranchId,
  isOverLimit,
  maxLengthMessage,
} from "@investorRequest/utils/SpecificValidations";
import { toEffectiveDateString } from "@investorRequest/utils/effectiveDate";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";
import C2DSFieldErrorMessages from "@investorRequest/components/C2DSFieldErrorMessages";

const MAX_BENEFICIARY_ACCOUNT_NAME = 140;
const MAX_BANK_NAME = 30;
const MAX_BANK_ACCOUNT_NUMBER = 35;
// IR-UI-Revamp_V1: SWIFT field max lengths
const MAX_SWIFT_ID = 11;
const MAX_IBAN = 34;
const MAX_BANK_ID = 20;
const MAX_BRANCH_ID = 10;

const useStyles = makeStyles((theme) => getDropdownWithIconStyles(theme));

interface Props {
  values: {
    bankName: string;
    beneficiaryAccountName: string;
    bankCountry: string;
    bankAccountNumber: string;
    intermediaryYesNo?: string;
    intermediarySwiftOrAba?: string;
    intermediaryBankName?: string;
    intermediaryBankCountry?: string;
    furtherCreditYesNo?: string;
    furtherCreditLine1?: string;
    furtherCreditLine2?: string;
    // IR-UI-Revamp_V1: New SWIFT fields
    SwiftId?: string;
    IBAN?: string;
    BankIdNbr?: string;
    BranchId?: string;
    effectiveDate?: string;
  };
  countriesOptions: ListOptionBase[];
  selectedCountry?: ListOptionBase;
  onSelectCountry(option: ListOptionBase): void;
  selectedIntermediaryCountry?: ListOptionBase;
  onSelectIntermediaryCountry(option: ListOptionBase): void;
  onChange(field: string, value: string): void;
  showErrors: boolean;
  inputXS: 12 | 6;
  disableBankCountry?: boolean;
}

const InvestorRequestSwiftFields: FunctionComponent<Props> = ({
  values,
  onChange,
  showErrors,
  inputXS,
  countriesOptions,
  selectedCountry,
  onSelectCountry,
  selectedIntermediaryCountry,
  onSelectIntermediaryCountry,
  disableBankCountry = false,
}) => {
  const classes = useStyles();
  const { selectedValueRef, dropdownOpen, isToggleOverflowing, selectKey, handleSelect, handleToggleButtonClick } =
    useDropdownOverflowTooltip({
      testId: "beneficiary-bank-country-select",
      selectedLabel: selectedCountry?.label,
    });

  // LPC-10289: C2DS validation errors
  const { hasFieldError, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache C2DS error states to ensure consistency between error prop and message display
  const hasSwiftIdC2DSError = hasFieldError("SwiftId");
  const hasIBANC2DSError = hasFieldError("IBAN");
  const hasBankIdC2DSError = hasFieldError("BankIdNbr");
  const hasBranchIdC2DSError = hasFieldError("BranchId");
  const hasBankAccountC2DSError = hasFieldError("bankAccountNumber");
  const isInvalidCountryForSwift = !!selectedCountry && selectedCountry.label.toLowerCase() === "united states";

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) => onChange("effectiveDate", value ? toEffectiveDateString(value) : ""),
    [onChange]
  );

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href="/assets/auth/css/wireformstyle.css" />
      </Helmet>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryAccountName}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BeneficiaryAccountName}
            dense={false}
            placeholder="*Beneficiary Account Name"
            variant="light"
            size="large"
            value={values.beneficiaryAccountName}
            onValueChange={(v) => onChange(fieldKeys.BeneficiaryAccountName, sanitizeAsciiName(v))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t)}
            error={
              (showErrors && !(values.beneficiaryAccountName || "").trim()) ||
              isOverLimit(values.beneficiaryAccountName, MAX_BENEFICIARY_ACCOUNT_NAME)
            }
          />
        </FieldTooltip>
        {showErrors && !(values.beneficiaryAccountName || "").trim() && (
          <Box className="ir-helper-text-warning">{validationMessages.beneficiaryAccountName.required}</Box>
        )}
        {isOverLimit(values.beneficiaryAccountName, MAX_BENEFICIARY_ACCOUNT_NAME) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("Account Name", MAX_BENEFICIARY_ACCOUNT_NAME)}</Box>
        )}
        {showErrors &&
          !!(values.beneficiaryAccountName || "").trim() &&
          values.beneficiaryAccountName.length <= MAX_BENEFICIARY_ACCOUNT_NAME &&
          /[^A-Za-z0-9\s]/.test(values.beneficiaryAccountName) && (
            <Box className="ir-helper-text-warning">{validationMessages.beneficiaryAccountName.invalid}</Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankName}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BankName}
            dense={false}
            placeholder="*Beneficiary Bank Name"
            variant="light"
            size="large"
            value={values.bankName}
            onValueChange={(v) => onChange(fieldKeys.BankName, sanitizeAsciiName(v))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t)}
            error={(showErrors && !(values.bankName || "").trim()) || isOverLimit(values.bankName, MAX_BANK_NAME)}
          />
        </FieldTooltip>
        {showErrors && !(values.bankName || "").trim() && (
          <Box className="ir-helper-text-warning">{validationMessages.bankName.required}</Box>
        )}
        {isOverLimit(values.bankName, MAX_BANK_NAME) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("Bank Name", MAX_BANK_NAME)}</Box>
        )}
        {showErrors &&
          !!(values.bankName || "").trim() &&
          values.bankName.length <= MAX_BANK_NAME &&
          /[^A-Za-z0-9\s]/.test(values.bankName) && (
            <Box className="ir-helper-text-warning">{validationMessages.bankName.invalid}</Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip
          titleNode={
            <Box>
              <Typography>Beneficiary Bank Country</Typography>
              {isToggleOverflowing && selectedCountry?.label ? <Typography>{selectedCountry.label}</Typography> : null}
            </Box>
          }
          disableHover={dropdownOpen}
        >
          <Box className={classes.bankCountryContainer}>
            <Box className={classes.bankCountryDropdown} ref={selectedValueRef}>
              <Box>
                <Singleselect
                  key={selectKey}
                  options={countriesOptions}
                  selectedOption={selectedCountry}
                  onSelect={(option) => {
                    clearFieldError("bankCountry");
                    onSelectCountry(option);
                    handleSelect();
                  }}
                  onToggleButtonClick={handleToggleButtonClick}
                  label={selectedCountry?.label || "*Beneficiary Bank Country"}
                  variant="contained"
                  size="large"
                  withSearch
                  testId="beneficiary-bank-country-select"
                  disabled={disableBankCountry}
                />
              </Box>
            </Box>
          </Box>
        </FieldTooltip>
        {showErrors && !selectedCountry && (
          <Box className="ir-helper-text-warning">{validationMessages.beneficiaryBankCountry.required}</Box>
        )}
        {isInvalidCountryForSwift && (
          <Box className="ir-helper-text-warning">{validationMessages.beneficiaryBankCountry.invalidForSwift}</Box>
        )}
        {!isInvalidCountryForSwift && <C2DSFieldErrorMessages fieldKey="bankCountry" />}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryBankAccountNumber}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BankAccountNumber}
            dense={false}
            placeholder="Beneficiary Bank Account #"
            variant="light"
            size="large"
            value={values.bankAccountNumber}
            onValueChange={(v) => {
              clearFieldError("bankAccountNumber");
              onChange(fieldKeys.BankAccountNumber, sanitizeAccountNumberInput(v, undefined, 10));
            }}
            sanitizeOnPaste={(t) => sanitizeAccountNumberInput(t, undefined, 10)}
            error={isOverLimit(values.bankAccountNumber, MAX_BANK_ACCOUNT_NUMBER) || hasBankAccountC2DSError}
          />
        </FieldTooltip>
        {isOverLimit(values.bankAccountNumber, MAX_BANK_ACCOUNT_NUMBER) && (
          <Box className="ir-helper-text-warning">
            {maxLengthMessage("Bank Account Number", MAX_BANK_ACCOUNT_NUMBER)}
          </Box>
        )}
        <C2DSFieldErrorMessages fieldKey="bankAccountNumber" />
      </Grid>
      {/* IR-UI-Revamp_V1: New SWIFT fields */}
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.Swift}>
          <ExtractionAwareInput
            fieldName={fieldKeys.SwiftId}
            dense={false}
            placeholder="*SWIFT"
            variant="light"
            size="large"
            value={values.SwiftId || ""}
            onValueChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("SwiftId");
              onChange(fieldKeys.SwiftId, sanitizeSwiftId(v));
            }}
            sanitizeOnPaste={(t) => sanitizeSwiftId(t)}
            error={
              (showErrors && (!values.SwiftId || !isValidSwiftId(values.SwiftId))) ||
              isOverLimit(values.SwiftId, MAX_SWIFT_ID) ||
              hasSwiftIdC2DSError
            }
          />
        </FieldTooltip>
        {showErrors && !values.SwiftId && (
          <Box className="ir-helper-text-warning">{validationMessages.swiftId.required}</Box>
        )}
        {isOverLimit(values.SwiftId, MAX_SWIFT_ID) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("SWIFT ID", MAX_SWIFT_ID)}</Box>
        )}
        {showErrors && !!values.SwiftId && values.SwiftId.length <= MAX_SWIFT_ID && !isValidSwiftId(values.SwiftId) && (
          <Box className="ir-helper-text-warning">{validationMessages.swiftId.invalid}</Box>
        )}
        {/* LPC-10289: C2DS validation error */}
        <C2DSFieldErrorMessages fieldKey="SwiftId" />
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.Iban}>
          <Input
            dense={false}
            placeholder="IBAN"
            variant="light"
            size="large"
            value={values.IBAN || ""}
            onChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("IBAN");
              onChange(fieldKeys.IBAN, sanitizeIBAN(v));
            }}
            sanitizeOnPaste={(t) => sanitizeIBAN(t)}
            error={
              (showErrors && !!values.IBAN && !isValidIBAN(values.IBAN)) ||
              isOverLimit(values.IBAN, MAX_IBAN) ||
              hasIBANC2DSError
            }
          />
        </FieldTooltip>
        {isOverLimit(values.IBAN, MAX_IBAN) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("IBAN", MAX_IBAN)}</Box>
        )}
        {showErrors && !!values.IBAN && values.IBAN.length <= MAX_IBAN && !isValidIBAN(values.IBAN) && (
          <Box className="ir-helper-text-warning">{validationMessages.iban.invalid}</Box>
        )}
        {/* LPC-10289: C2DS validation error */}
        <C2DSFieldErrorMessages fieldKey="IBAN" />
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankId}>
          <Input
            dense={false}
            placeholder="Bank ID"
            variant="light"
            size="large"
            value={values.BankIdNbr || ""}
            onChange={(v) => {
              clearFieldError("BankIdNbr");
              onChange(fieldKeys.BankIdNbr, sanitizeBankId(v));
            }}
            sanitizeOnPaste={(t) => sanitizeBankId(t)}
            error={
              (showErrors && !!values.BankIdNbr && !isValidBankId(values.BankIdNbr)) ||
              isOverLimit(values.BankIdNbr, MAX_BANK_ID) ||
              hasBankIdC2DSError
            }
          />
        </FieldTooltip>
        {isOverLimit(values.BankIdNbr, MAX_BANK_ID) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("Bank ID", MAX_BANK_ID)}</Box>
        )}
        {showErrors &&
          !!values.BankIdNbr &&
          values.BankIdNbr.length <= MAX_BANK_ID &&
          !isValidBankId(values.BankIdNbr) && (
            <Box className="ir-helper-text-warning">{validationMessages.bankId.invalid}</Box>
          )}
        <C2DSFieldErrorMessages fieldKey="BankIdNbr" />
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BranchId}>
          <Input
            dense={false}
            placeholder="Branch ID"
            variant="light"
            size="large"
            value={values.BranchId || ""}
            onChange={(v) => {
              clearFieldError("BranchId");
              onChange(fieldKeys.BranchId, sanitizeBranchId(v));
            }}
            sanitizeOnPaste={(t) => sanitizeBranchId(t)}
            error={
              (showErrors && !!values.BranchId && !isValidBranchId(values.BranchId)) ||
              isOverLimit(values.BranchId, MAX_BRANCH_ID) ||
              hasBranchIdC2DSError
            }
          />
        </FieldTooltip>
        {isOverLimit(values.BranchId, MAX_BRANCH_ID) && (
          <Box className="ir-helper-text-warning">{maxLengthMessage("Branch ID", MAX_BRANCH_ID)}</Box>
        )}
        {showErrors &&
          !!values.BranchId &&
          values.BranchId.length <= MAX_BRANCH_ID &&
          !isValidBranchId(values.BranchId) && (
            <Box className="ir-helper-text-warning">{validationMessages.branchId.invalid}</Box>
          )}
        <C2DSFieldErrorMessages fieldKey="BranchId" />
      </Grid>
      <IntermediaryFields
        yesNoValue={values.intermediaryYesNo || ""}
        swiftOrAba={values.intermediarySwiftOrAba || ""}
        bankName={values.intermediaryBankName || ""}
        countriesOptions={countriesOptions}
        selectedCountry={selectedIntermediaryCountry}
        onSelectCountry={onSelectIntermediaryCountry}
        onChangeYesNo={(v) => {
          onChange("intermediaryYesNo", v);
          // IR-UI-Revamp_V1: Clear intermediary fields when "No" is selected
          if (v === "no") {
            onChange("intermediarySwiftOrAba", "");
            onChange("intermediaryBankName", "");
            onChange("intermediaryBankCountry", "");
          }
        }}
        onChangeSwiftOrAba={(v) => onChange("intermediarySwiftOrAba", v)}
        onChangeBankName={(v) => onChange("intermediaryBankName", v)}
        showErrors={showErrors}
      />
      <FurtherCreditFields
        yesNoValue={values.furtherCreditYesNo || ""}
        line1={values.furtherCreditLine1 || ""}
        line2={values.furtherCreditLine2 || ""}
        onChangeYesNo={(v) => {
          onChange("furtherCreditYesNo", v);
          // IR-UI-Revamp_V1: Clear further credit fields when "No" is selected
          if (v === "no") {
            onChange("furtherCreditLine1", "");
            onChange("furtherCreditLine2", "");
          }
        }}
        onChangeLine1={(v) => onChange("furtherCreditLine1", v)}
        onChangeLine2={(v) => onChange("furtherCreditLine2", v)}
        showErrors={showErrors}
      />
      <Grid item xs={inputXS} data-testid="investor-request-datepicker">
        <FieldTooltip title={FIELD_TITLES.DateInstructionsBecomeEffective}>
          <InvestorRequestDatepicker value={values.effectiveDate || ""} onChange={handleDatepickerChange} />
        </FieldTooltip>
      </Grid>
      <Grid item xs={12}>
        <FieldTooltip title={FIELD_TITLES.AdditionalNote}>
          <InvestorRequestTextarea
            placeholder="Please include any additional notes"
            value={(values as any).freeText || ""}
            onChange={(v: string) => onChange("freeText", v)}
            error={isOverLimit((values as any).freeText, 8000)}
          />
        </FieldTooltip>
        {isOverLimit((values as any).freeText, 8000) && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Additional Notes", 8000)}
          </Box>
        )}
      </Grid>
    </>
  );
};

export default memo(InvestorRequestSwiftFields);
